import React, { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, addDoc, updateDoc, setDoc, deleteDoc, doc, query, where, writeBatch } from 'firebase/firestore';
import { db, getCompanyId } from '../firebase';
import Navbar from '../components/Navbar';

const TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const totalMinutes = i * 15;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const displayMinute = String(minute).padStart(2, '0');
  return { label: `${displayHour}:${displayMinute} ${ampm}`, value: i, hour, minute };
});
// Shifts may end at midnight (value 96) — "Evening Shift, 4:00 PM → 12:00 AM"
const END_TIMES = [...TIMES.slice(1), { label: '12:00 AM', value: 96, hour: 24, minute: 0 }];
const timeLabel = (v) => (v === 96 ? '12:00 AM' : TIMES[v]?.label);

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/* Shifts are reusable templates: a name and a time range, nothing tied to a
   date. The Schedule tab puts them on actual days and assigns employees. */
export default function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);

  const [shiftName, setShiftName] = useState('');
  const [startTime, setStartTime] = useState(32); // 8:00 AM
  const [endTime, setEndTime] = useState(64);     // 4:00 PM
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [graceInput, setGraceInput] = useState('5');
  const [savingGrace, setSavingGrace] = useState(false);
  const [graceMsg, setGraceMsg] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const companyId = await getCompanyId();
    const [shiftsSnap, settingsSnap] = await Promise.all([
      getDocs(query(collection(db, 'shifts'), where('companyId', '==', companyId))),
      getDoc(doc(db, 'settings', companyId)),
    ]);
    setShifts(shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    const grace = settingsSnap.exists() ? settingsSnap.data().graceMinutes : undefined;
    setGraceInput(String(typeof grace === 'number' ? grace : 5));
    setLoading(false);
  };

  const templates = shifts.filter(s => s.type === 'template');
  // Old day-tied weekly shifts. They still gate employee clock-ins until
  // deleted, so they stay visible here for cleanup.
  const legacy = shifts.filter(s => s.type === 'repeating');

  const templateName = (t) => t.name || `${timeLabel(t.startTime)} → ${timeLabel(t.endTime)}`;

  const resetForm = () => {
    setShiftName('');
    setStartTime(32);
    setEndTime(64);
    setEditingShift(null);
    setError('');
  };

  const handleEdit = (shift) => {
    setShiftName(shift.name || '');
    setStartTime(shift.startTime ?? 32);
    setEndTime(shift.endTime ?? 64);
    setEditingShift(shift);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (endTime <= startTime) { setError('End time must be after start time'); return; }

    setSaving(true);
    try {
      const companyId = await getCompanyId();
      const end = END_TIMES.find(t => t.value === endTime);
      const shiftData = {
        name: shiftName.trim(),
        type: 'template',
        startTime,
        endTime,
        startHour: TIMES[startTime].hour,
        startMinute: TIMES[startTime].minute,
        endHour: end.hour,
        endMinute: end.minute,
        date: null,
        employeeIds: null,
        employeesByDay: null,
        companyId,
        updatedAt: new Date(),
      };

      if (editingShift) {
        await updateDoc(doc(db, 'shifts', editingShift.id), shiftData);
        // A template edit flows into days that haven't happened yet; past
        // days keep the times that were actually worked.
        const today = todayStr();
        const future = shifts.filter(s =>
          s.shiftTemplateId === editingShift.id && s.date && s.date >= today);
        for (let i = 0; i < future.length; i += 400) {
          const batch = writeBatch(db);
          for (const s of future.slice(i, i + 400)) {
            batch.update(doc(db, 'shifts', s.id), {
              name: shiftData.name,
              startTime, endTime,
              startHour: shiftData.startHour, startMinute: shiftData.startMinute,
              endHour: shiftData.endHour, endMinute: shiftData.endMinute,
              updatedAt: new Date(),
            });
          }
          await batch.commit();
        }
      } else {
        await addDoc(collection(db, 'shifts'), { ...shiftData, createdAt: new Date() });
      }

      await loadData();
      resetForm();
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleSaveGrace = async () => {
    const grace = parseInt(graceInput, 10);
    if (isNaN(grace) || grace < 0) {
      setGraceMsg('Enter the threshold in minutes.');
      return;
    }
    setSavingGrace(true);
    setGraceMsg('');
    try {
      const companyId = await getCompanyId();
      await setDoc(doc(db, 'settings', companyId), { graceMinutes: grace }, { merge: true });
      setGraceInput(String(grace));
      setGraceMsg(`Saved — clocking in more than ${grace} minute${grace === 1 ? '' : 's'} before or after a shift start counts as early or late.`);
    } catch (err) {
      setGraceMsg(err.message);
    }
    setSavingGrace(false);
  };

  const handleDelete = async (shift) => {
    const name = templateName(shift);
    const scheduled = shifts.filter(s => s.shiftTemplateId === shift.id && s.date && s.date >= todayStr()).length;
    const extra = scheduled > 0
      ? ` It is scheduled on ${scheduled} upcoming day${scheduled === 1 ? '' : 's'} — those stay on the calendar until you remove them in the Schedule tab.`
      : '';
    if (window.confirm(`Delete "${name}"?${extra}`)) {
      await deleteDoc(doc(db, 'shifts', shift.id));
      await loadData();
    }
  };

  const handleDeleteLegacy = async (shift) => {
    if (window.confirm(`Delete the old weekly shift "${shift.name}"? Employees will no longer be scheduled on its days.`)) {
      await deleteDoc(doc(db, 'shifts', shift.id));
      await loadData();
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Shifts</h1>
            <div style={styles.subtitle}>
              Reusable time templates — put them on days in the Schedule tab.
            </div>
          </div>
          <button style={styles.addButton} onClick={() => { resetForm(); setShowForm(true); }}>
            + New Shift
          </button>
        </div>

        {!loading && (
          <div style={styles.graceCard}>
            <div style={styles.graceInfo}>
              <div style={styles.graceTitle}>Early / Late Threshold</div>
              <div style={styles.graceHint}>
                Clocking in more than this many minutes before or after a shift's start time counts as early or late. Employees are asked for a reason either way.
              </div>
              {graceMsg && <div style={styles.graceMsg}>{graceMsg}</div>}
            </div>
            <div style={styles.graceControls}>
              <span style={styles.graceSign}>±</span>
              <input
                style={styles.graceInputBox}
                value={graceInput}
                maxLength={3}
                onChange={e => setGraceInput(e.target.value.replace(/[^0-9]/g, ''))}
              />
              <span style={styles.graceUnit}>minutes</span>
              <button style={styles.graceSaveBtn} onClick={handleSaveGrace} disabled={savingGrace}>
                {savingGrace ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>{editingShift ? 'Edit Shift' : 'New Shift'}</h3>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleSave}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Shift Name (optional)</label>
                  <input
                    style={styles.input}
                    placeholder="e.g. Morning Shift"
                    value={shiftName}
                    onChange={e => setShiftName(e.target.value)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Time</label>
                  <div style={styles.timeRow}>
                    <select style={styles.select} value={startTime} onChange={e => setStartTime(Number(e.target.value))}>
                      {TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <span style={{ color: '#999', fontSize: 18, padding: '0 4px' }}>→</span>
                    <select style={styles.select} value={endTime} onChange={e => setEndTime(Number(e.target.value))}>
                      {END_TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {editingShift && (
                <div style={styles.formHint}>
                  Changes apply to this template and any upcoming scheduled days that use it. Past days are not changed.
                </div>
              )}

              <div style={styles.formButtons}>
                <button type="button" style={styles.cancelBtn}
                  onClick={() => { setShowForm(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Shift'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : templates.length === 0 ? (
          <div style={styles.empty}>No shifts yet. Create one, then schedule it on the calendar!</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Shift Name', 'Time', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.map(shift => (
                <tr key={shift.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.shiftName}>{shift.name || <span style={{ color: '#999' }}>Unnamed shift</span>}</div>
                  </td>
                  <td style={styles.td}>{timeLabel(shift.startTime)} → {timeLabel(shift.endTime)}</td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button style={styles.editBtn} onClick={() => handleEdit(shift)}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(shift)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && legacy.length > 0 && (
          <div style={styles.legacyCard}>
            <div style={styles.legacyTitle}>Legacy weekly shifts</div>
            <div style={styles.legacyHint}>
              These older shifts are tied to weekdays and still schedule employees every week.
              Recreate them as shift templates above, put them on days in the Schedule tab, then delete them here.
            </div>
            {legacy.map(shift => (
              <div key={shift.id} style={styles.legacyRow}>
                <div>
                  <div style={styles.shiftName}>{shift.name}</div>
                  <div style={styles.legacyMeta}>
                    {timeLabel(shift.startTime)} → {timeLabel(shift.endTime)}
                    {' · '}
                    {Object.keys(shift.employeesByDay || {}).join(', ') || 'No days'}
                  </div>
                </div>
                <button style={styles.deleteBtn} onClick={() => handleDeleteLegacy(shift)}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f'
  },

  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },

  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    margin: 0
  },

  subtitle: {
    color: '#999',
    fontSize: 14,
    marginTop: 4
  },

  addButton: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  formCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  },

  graceCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: '18px 24px',
    marginBottom: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap'
  },

  graceInfo: {
    flex: 1,
    minWidth: 280
  },

  graceTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 4
  },

  graceHint: {
    color: '#999',
    fontSize: 13,
    lineHeight: 1.4
  },

  graceMsg: {
    color: '#4ade80',
    fontSize: 13,
    marginTop: 6
  },

  graceControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },

  graceSign: {
    color: '#4f46e5',
    fontSize: 20,
    fontWeight: 'bold'
  },

  graceInputBox: {
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    width: 60,
    textAlign: 'center'
  },

  graceUnit: {
    color: '#999',
    fontSize: 14
  },

  graceSaveBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13
  },

  formTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 16
  },

  formHint: {
    color: '#999',
    fontSize: 13,
    lineHeight: 1.4,
    marginBottom: 16
  },

  error: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14
  },

  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
    alignItems: 'start'
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16
  },

  label: {
    color: '#999',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1
  },

  input: {
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none'
  },

  select: {
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer'
  },

  timeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },

  formButtons: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginTop: 20
  },

  cancelBtn: {
    backgroundColor: '#2a2a2a',
    color: '#999',
    border: 'none',
    borderRadius: 10,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  saveBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    overflow: 'hidden'
  },

  th: {
    padding: '14px 20px',
    textAlign: 'left',
    color: '#999',
    fontSize: 13,
    fontWeight: '600',
    borderBottom: '1px solid #2a2a2a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },

  tr: {
    borderBottom: '1px solid #2a2a2a'
  },

  td: {
    padding: '14px 20px',
    color: '#fff',
    fontSize: 14
  },

  shiftName: {
    fontWeight: 'bold',
    marginBottom: 2
  },

  actions: {
    display: 'flex',
    gap: 8
  },

  editBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13
  },

  deleteBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13
  },

  legacyCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginTop: 24,
    border: '1px solid #3a2f1a'
  },

  legacyTitle: {
    color: '#d5a94f',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 6
  },

  legacyHint: {
    color: '#999',
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 12
  },

  legacyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '12px 0',
    borderTop: '1px solid #2a2a2a'
  },

  legacyMeta: {
    color: '#999',
    fontSize: 13,
    marginTop: 2
  },

  empty: {
    color: '#999',
    textAlign: 'center',
    padding: 40
  }
};
